import { useState } from 'react';
import { MAX_POLL_OPTION_TEXT, MAX_POLL_OPTIONS, MAX_POLL_QUESTION } from '@avslack/shared';
import { useCreatePoll } from '../data/polls';
import { Dialog, useAnimatedClose } from './Dialog';
import { Icon } from './Icon';

/**
 * SZAVAZAS letrehozasa (versenyaudit U4).
 *
 * Kerdes, 2-20 opcio (ujabb mezo a "+ Ujabb lehetoseg" gombbal, es magatol is megjelenik, ha az utolso kitoltve),
 * egy/tobb valasztas, nevtelenseg es hatarido. A hatarido gyors
 * valasztoval - a "holnap reggelig" a gyakori eset, nem egy pontos datum.
 */

const DEADLINES: { label: string; at: () => Date | null }[] = [
  { label: 'Nincs', at: () => null },
  { label: '1 óra', at: () => new Date(Date.now() + 60 * 60_000) },
  {
    label: 'Ma 17:00',
    at: () => {
      const d = new Date();
      d.setHours(17, 0, 0, 0);
      return d;
    },
  },
  {
    label: 'Holnap 9:00',
    at: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  { label: '1 hét', at: () => new Date(Date.now() + 7 * 24 * 60 * 60_000) },
];

export function PollDialog({
  channelId,
  onClose: closeNow,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const onClose = useAnimatedClose(closeNow);
  const create = useCreatePoll(channelId);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [deadline, setDeadline] = useState('Nincs');
  // Ami mar elmult (pl. "Ma 17:00" este), azt fel sem kinaljuk.
  const deadlines = DEADLINES.filter((option) => {
    const at = option.at();
    return !at || at.getTime() > Date.now();
  });

  const filled = options.map((o) => o.trim()).filter(Boolean);
  const duplicate = new Set(filled.map((o) => o.toLowerCase())).size !== filled.length;
  const deadlineAt = (deadlines.find((o) => o.label === deadline) ?? deadlines[0]!).at();
  const deadlinePast = deadlineAt !== null && deadlineAt.getTime() <= Date.now();
  const problem = !question.trim()
    ? 'Írd be a kérdést.'
    : filled.length < 2
      ? 'Legalább két válaszlehetőség kell.'
      : duplicate
        ? 'Két válaszlehetőség nem lehet ugyanaz.'
        : deadlinePast
          ? 'Ez a határidő már elmúlt.'
          : null;

  const setOption = (index: number, value: string) =>
    setOptions((current) => {
      const next = current.map((o, i) => (i === index ? value : o));
      // Az utolso mezo kitoltesekor jon egy uj, ures - amig van hely.
      if (index === next.length - 1 && value.trim() && next.length < MAX_POLL_OPTIONS) {
        next.push('');
      }
      return next;
    });

  const submit = () => {
    if (problem) return;
    create.mutate(
      {
        question: question.trim(),
        options: filled,
        multiple,
        anonymous,
        closesAt: deadlineAt?.toISOString() ?? null,
      },
      { onSuccess: onClose },
    );
  };

  const input =
    'w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-sm outline-none focus:border-accent';

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Szavazás"
      description="A csatorna tagjai egy kattintással szavazhatnak, az eredmény élőben látszik."
    >
      <label className="block text-xs font-medium text-ink-muted">
        Kérdés
        <input
          autoFocus
          value={question}
          maxLength={MAX_POLL_QUESTION}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="pl. Mikor legyen a csapatebéd?"
          className={`mt-1 ${input}`}
        />
      </label>

      <fieldset className="mt-3">
        <legend className="text-xs font-medium text-ink-muted">Válaszlehetőségek</legend>
        <div className="mt-1 space-y-1.5">
          {options.map((option, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <input
                value={option}
                maxLength={MAX_POLL_OPTION_TEXT}
                onChange={(e) => setOption(index, e.target.value)}
                placeholder={`${index + 1}. lehetőség`}
                aria-label={`${index + 1}. lehetőség`}
                className={input}
              />
              {options.length > 2 && (
                <button
                  type="button"
                  onClick={() => setOptions((current) => current.filter((_, i) => i !== index))}
                  aria-label={`${index + 1}. lehetőség törlése`}
                  className="rounded-control p-1.5 text-ink-faint hover:text-ink"
                >
                  <Icon name="bezaras" size="sm" />
                </button>
              )}
            </div>
          ))}
        </div>
        {/*
          Az ujabb lehetoseg GOMBBAL is felveheto, nem csak az utolso mezo kitoltesekor:
          aki nem tudja, hogy az utolso mezo "szaporodik", csak ket opciot lat.
        */}
        {options.length < MAX_POLL_OPTIONS && (
          <button
            type="button"
            onClick={() => setOptions((current) => [...current, ''])}
            className="mt-1.5 rounded-control px-2 py-1 text-xs font-semibold text-accent hover:bg-surface-hover"
          >
            + Újabb lehetőség
          </button>
        )}
        <p className="mt-1 text-2xs text-ink-faint">
          {filled.length} lehetőség kitöltve (legfeljebb {MAX_POLL_OPTIONS}).
        </p>
      </fieldset>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={multiple}
            onChange={(e) => setMultiple(e.target.checked)}
          />
          Több is választható
        </label>
        <label className="flex items-center gap-1.5" title="A szavazók neve senkinek nem látszik">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          Névtelen
        </label>
      </div>

      <div className="mt-3">
        <span className="text-xs font-medium text-ink-muted">Határidő</span>
        <div className="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Határidő">
          {deadlines.map((option) => (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={deadline === option.label}
              onClick={() => setDeadline(option.label)}
              className={[
                'rounded-pill border px-2.5 py-0.5 text-xs transition-colors',
                deadline === option.label
                  ? 'border-accent bg-accent-soft font-semibold text-accent'
                  : 'border-border-soft text-ink-muted hover:border-border-strong',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {(problem && question) || create.isError ? (
        <p role="alert" className="mt-3 text-xs text-danger">
          {create.isError
            ? create.error instanceof Error
              ? create.error.message
              : 'A szavazás nem jött létre.'
            : problem}
        </p>
      ) : null}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control px-3 py-2 text-sm text-ink-muted hover:text-ink"
        >
          Mégse
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={Boolean(problem) || create.isPending}
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink disabled:opacity-50"
        >
          {create.isPending ? 'Küldés…' : 'Szavazás indítása'}
        </button>
      </div>
    </Dialog>
  );
}
